import React, { useState } from 'react';

import { Button } from '@/components/ui/button';

import { useDeviceType } from '@/hooks/useDeviceType';

// images
import imgOnboarding1 from '@/assets/images/onboarding-1.png';
import imgOnboarding2 from '@/assets/images/onboarding-2.png';
import imgOnboarding2_1 from '@/assets/images/onboarding-2_1.png';
import imgOnboarding3 from '@/assets/images/onboarding-3.png';
import imgOnboarding4 from '@/assets/images/onboarding-4.png';
import imgOnboarding5 from '@/assets/images/onboarding-5.png';

const steps = [
  {
    img: imgOnboarding1,
    title: 'Create a budget',
    description:
      'Start by setting your financial goals. Define your Budget Maximum Expense Value (BMEV) and Budget Maximum Revenue Target (BMRT) to track how much you plan to spend and earn during a specific period.'
  },
  {
    img: imgOnboarding2,
    title: 'Add a Bank Account',
    description:
      'Link a Bank Account to manage your cash flow. Set the initial balance and use it to allocate invoices and track financial movements.'
  },
  {
    img: imgOnboarding2_1,
    title: 'Managerial Accounting Center',
    description:
      'MAEs (Accounting Centers & Categories) are fully independent, no rigid hierarchies. Each MAE has its own limit, but all must stay within the overall BMEV and BMRT.'
  },
  {
    img: imgOnboarding3,
    title: 'Create an Accounting Center',
    description:
      'Customize Accounting Centers based on your business units like departments, product and services, projects, or contract. Assign each AC its own Accounting Center Maximum Expense Value (ACMEV) and Accounting Center Revenue Target (ACRT).'
  },
  {
    img: imgOnboarding4,
    title: 'Define Expense & Revenue Categories',
    description:
      'Classify all transactions using Expense Categories and Revenue Categories. Each category can be assigned a maximum or target value, ensuring all your income and expenses are properly tracked.'
  },
  {
    img: imgOnboarding5,
    title: 'Upload an Invoices',
    description:
      "Upload invoices and allocate them across the entities you've created. Each invoice can be marked as Expense or Revenue and linked to the relevant MAEs, giving you real-time insights into your budget."
  }
];

interface OnboardingCarouselProps {
  onComplete: () => void;
  onSkip?: () => void;
}

const OnboardingCarousel: React.FC<OnboardingCarouselProps> = ({ onComplete, onSkip }) => {
  const [index, setIndex] = useState(0);

  const { isMobileView } = useDeviceType();

  const handleNextClick = () => {
    if (index === steps.length - 1) {
      onComplete();
      return;
    }

    if (index < steps.length) setIndex((prev) => prev + 1);
  };

  const handlePreviousClick = () => {
    if (index > 0) setIndex((prev) => prev - 1);
  };

  return (
    <div className={`h-[80%] ${isMobileView ? 'pt-32 short-h:pt-16' : ''}  flex-1 md:h-full md:pb-6 flex flex-col`}>
      <div
        key={index}
        className={` ${!isMobileView ? 'bg-white border rounded-[24px] py-6 min-h-[480px] mt-10' : 'flex-1'}`}
      >
        <img className="mb-8 mx-auto" src={steps[index].img} alt="onboarding-1-img" />
        <h2 className={`${isMobileView ? 'text-xl' : 'text-2xl'} mb-2 font-bold text-center`}>{steps[index].title}</h2>
        <p
          className={`text-center px-5 ${isMobileView ? 'text-sm leading-[17.07px]' : 'text-lg leading-[22px] px-10'} max-w-[900px] mx-auto  text-secondary`}
        >
          {steps[index].description}
        </p>
      </div>

      {/* Steps Progress */}
      {isMobileView && (
        <div className="w-fit mx-auto mt-10 flex items-center gap-1">
          {steps.map((_, i) => {
            return (
              <span
                key={i}
                className={`h-[4px] ${index === i ? 'w-[34px]' : 'w-[14px]'} ${index === i ? 'bg-primary' : 'bg-lightGray'} rounded-full`}
              ></span>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between gap-4 px-4 mt-10">
        {!isMobileView ? (
          <>
            <Button
              onClick={() => {
                if (onSkip) onSkip();
              }}
              variant={'text'}
              className="py-5 font-semibold text-lg"
            >
              Skip
            </Button>
            <div className="flex items-center gap-7">
              <p>
                {index + 1} of {steps.length}
              </p>
              <Button
                disabled={index === 0}
                onClick={handlePreviousClick}
                className="rounded-[24px] uppercase text-lg h-[64px] px-6"
                variant={'outline'}
              >
                Previous
              </Button>
              <Button
                disabled={index === steps.length}
                onClick={handleNextClick}
                className="rounded-[24px] uppercase text-lg h-[64px] px-6"
              >
                Next
              </Button>
            </div>
          </>
        ) : (
          <>
            <Button
              disabled={index === 0}
              onClick={handlePreviousClick}
              className="rounded-full uppercase text-base flex-1"
              variant={'outline'}
            >
              Previous
            </Button>
            <Button
              disabled={index === steps.length}
              onClick={handleNextClick}
              className="rounded-full uppercase text-base flex-1"
            >
              Next
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default OnboardingCarousel;
